import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { useAuth } from "@/firebase/useAuth";
import { useLocation } from "react-router-dom";

export type QuickReply = {
  label: string;
  value: string;
};

export type ActionType = 
  | "START_LESSON" 
  | "OPEN_DOMAIN" 
  | "GO_TO_JOURNAL" 
  | "GO_TO_RESILIENCE"
  | "GO_TO_LEARNING"
  | "GO_TO_DAILY_CHALLENGE"
  | "GO_TO_PROFILE";

export type StructuredAction = {
  label: string;
  action: ActionType;
  params?: Record<string, string>;
};

export type Message = {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  timestamp: number;
  quickReplies?: QuickReply[];
  suggestions?: StructuredAction[];
};

export type RecentEvent = {
  type: "CHECKIN_SUBMITTED" | "JOURNAL_SUBMITTED";
  entryId: string;
  text?: string;
  score?: number;
};

type ZillAiContextType = {
  isOpen: boolean;
  isMinimized: boolean;
  messages: Message[];
  conversationId: string | null;
  recentEvent: RecentEvent | null;
  
  openChat: (event?: RecentEvent) => void;
  closeChat: () => void;
  minimizeChat: () => void;
  sendMessage: (content: string) => Promise<void>;
  clearMessages: () => void;
  setQuickReply: (reply: string) => void;
  loadConversation: (convId: string, msgs: Message[]) => void;
};

const ZillAiContext = createContext<ZillAiContextType | undefined>(undefined);

export function ZillAiProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [recentEvent, setRecentEvent] = useState<RecentEvent | null>(null);

  const openChat = useCallback((event?: RecentEvent) => {
    setIsOpen(true);
    setIsMinimized(false);
    
    if (event) {
      setRecentEvent(event);
      
      // Add automatic feedback offer message
      const feedbackMessage: Message = {
        id: `system-${Date.now()}`,
        role: "assistant",
        content: event.type === "CHECKIN_SUBMITTED" 
          ? "Nice job checking in! Want feedback on what you wrote?"
          : "Great work on your journal entry! Would you like some feedback?",
        timestamp: Date.now(),
        quickReplies: [
          { label: "Yes, give feedback", value: "Yes, give feedback" },
          { label: "Not now", value: "Not now" }
        ]
      };
      
      setMessages([feedbackMessage]);
    }
  }, []);

  const closeChat = useCallback(async () => {
    // Assess conversation for safety before closing
    if (user && conversationId && messages.length > 0) {
      try {
        console.log("[ZillAi] Closing chat, assessing conversation:", conversationId);
        const token = await user.getIdToken();
        
        // Fetch CSRF token
        const csrfResponse = await fetch("/api/csrf-token", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const { csrfToken } = await csrfResponse.json();
        
        const response = await fetch("/api/zillai/conversation-end", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({ conversationId }),
        });
        
        if (!response.ok) {
          console.error("[ZillAi] Assessment failed:", response.status, response.statusText);
        } else {
          const result = await response.json();
          console.log("[ZillAi] Assessment complete:", result);
        }
      } catch (error) {
        console.error("[ZillAi] Failed to assess conversation on close:", error);
      }
    } else {
      console.log("[ZillAi] Closing without assessment (no user/conversation/messages)");
    }
    
    setIsOpen(false);
    setIsMinimized(false);
  }, [user, conversationId, messages]);

  const minimizeChat = useCallback(() => {
    setIsMinimized(true);
  }, []);

  const sendMessage = useCallback(async (content: string) => {
    if (!user) return;
    
    // Add user message immediately
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: "user",
      content,
      timestamp: Date.now()
    };
    
    setMessages(prev => [...prev, userMessage]);

    try {
      // Call backend API (keep recentEvent for context throughout conversation)
      const token = await user.getIdToken();
      
      // Fetch CSRF token
      const csrfResponse = await fetch("/api/csrf-token", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const { csrfToken } = await csrfResponse.json();
      
      const response = await fetch("/api/zillai/message", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          message: content,
          conversationId: conversationId,
          currentRoute: location.pathname,
          recentEvent: recentEvent,
          appMapVersion: "1.0.0",
        }),
      });

      if (!response.ok) {
        // Handle interaction limit reached
        if (response.status === 403) {
          try {
            const errorData = await response.json();
            if (errorData.error === 'INTERACTION_LIMIT_REACHED') {
              // Build upgrade suggestions based on tier
              const suggestions = errorData.upgradeOptions?.map((option: any) => ({
                label: option.tier === 'pro' 
                  ? `Upgrade to Pro (${option.price}) - ${option.messages} conversations/month`
                  : `Upgrade to Pro+ (${option.price}) - ${option.messages} conversations/month`,
                action: "GO_TO_PROFILE",
              })) || [];
              
              // Add helpful upgrade message
              const upgradeMessage: Message = {
                id: `limit-${Date.now()}`,
                role: "assistant",
                content: errorData.message,
                timestamp: Date.now(),
                suggestions,
              };
              setMessages(prev => [...prev, upgradeMessage]);
              return;
            }
          } catch (e) {
            // Fall through to generic error
          }
        }
        
        // Handle profanity filter or other 400 errors
        if (response.status === 400) {
          try {
            const errorData = await response.json();
            const errorMessage: Message = {
              id: `error-${Date.now()}`,
              role: "assistant",
              content: errorData.error || "Sorry, I couldn't process that message. Please try rephrasing.",
              timestamp: Date.now(),
            };
            setMessages(prev => [...prev, errorMessage]);
            return;
          } catch (e) {
            // Fall through to generic error
          }
        }

        throw new Error("Failed to get response");
      }

      const data = await response.json();
      
      // Update conversation ID
      if (data.conversationId && !conversationId) {
        setConversationId(data.conversationId);
      }

      // Add assistant message
      const assistantMessage: Message = {
        id: `assistant-${Date.now()}`,
        role: "assistant",
        content: data.reply || data.message,
        timestamp: Date.now(),
        suggestions: data.suggestions,
        quickReplies: data.quickReplies,
      };
      
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      // Check if backend is not running
      const isConnectionError = error instanceof TypeError && error.message.includes("fetch");
      
      // Add error message
      const errorMessage: Message = {
        id: `error-${Date.now()}`,
        role: "assistant",
        content: isConnectionError 
          ? "I'm currently offline. Please make sure the backend server is running (npm run dev in the server folder)."
          : "Sorry, I'm having trouble responding right now. Please try again in a moment.",
        timestamp: Date.now()
      };
      
      setMessages(prev => [...prev, errorMessage]);
    }
  }, [user, recentEvent, conversationId, location.pathname]);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    setRecentEvent(null);
  }, []);

  const setQuickReply = useCallback((reply: string) => {
    // Handle quick reply selection
    sendMessage(reply);
  }, [sendMessage]);

  const loadConversation = useCallback((convId: string, msgs: Message[]) => {
    setConversationId(convId);
    setMessages(msgs);
    setRecentEvent(null);
  }, []);

  return (
    <ZillAiContext.Provider
      value={{
        isOpen,
        isMinimized,
        messages,
        conversationId,
        recentEvent,
        openChat,
        closeChat,
        minimizeChat,
        sendMessage,
        clearMessages,
        setQuickReply,
        loadConversation
      }}
    >
      {children}
    </ZillAiContext.Provider>
  );
}

export function useZillAi() {
  const context = useContext(ZillAiContext);
  if (!context) {
    throw new Error("useZillAi must be used within ZillAiProvider");
  }
  return context;
}
